import React from 'react';

import { Grid, Stack, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import dayjs, { Dayjs } from 'dayjs';

import FormSection from 'components/condition-list/FormSection';
import AmountField from 'components/form/AmountField';
import TextArea from 'components/form/TextArea';
import { ExpenseLabel, ExpenseStore, ExpenseSuggestion } from 'stores/ExpenseStoreAdapter';

import ExpenseLabelSelect from './ExpenseLabelSelect';
import ExpenseNameAutocomplete from './ExpenseNameAutocomplete';
import ExpenseStoreSelect from './ExpenseStoreSelect';

const NOTE_MAX = 1024;

export type ExpenseFormValues = {
  /** 購入日（yyyy-MM-dd） */
  purchasedOn: string;
  name: string;
  /** 金額（数値文字列、空=未入力） */
  amount: string;
  /** 数量（数値文字列、空=未入力） */
  quantity: string;
  storeId: string;
  labelId: string;
  note: string;
};

export type ExpenseFormErrors = Partial<Record<keyof ExpenseFormValues, string>>;

type Props = {
  values: ExpenseFormValues;
  onChange: (values: ExpenseFormValues) => void;
  stores: ExpenseStore[];
  labels: ExpenseLabel[];
  onStoreCreated: (store: ExpenseStore) => void;
  onLabelCreated: (label: ExpenseLabel) => void;
  errors: ExpenseFormErrors;
  /** 参照専用（詳細表示中で未編集）か */
  disabled?: boolean;
};

/**
 * 経費の登録/編集フォーム（ConsoleDetailDrawer 内で使用）。
 * - 購入日（初期値=現在日・必須）
 * - 経費名（候補オートコンプリート・選択で他項目プレフィル）
 * - 金額（0以上整数・必須）/ 数量（1以上整数・必須・初期値1）
 * - 店舗（必須・その場で新規作成可・defaultLabelId 自動反映）
 * - 品目ラベル（必須・その場で新規作成可）
 * - メモ（任意 max1024）
 */
const ExpenseForm: React.FC<Props> = ({
  values,
  onChange,
  stores,
  labels,
  onStoreCreated,
  onLabelCreated,
  errors,
  disabled = false,
}) => {
  const set = <K extends keyof ExpenseFormValues>(key: K, value: ExpenseFormValues[K]) =>
    onChange({ ...values, [key]: value });

  const dateValue: Dayjs | null = values.purchasedOn ? dayjs(values.purchasedOn) : null;

  const handleDateChange = (newValue: Dayjs | null) => {
    set('purchasedOn', newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '');
  };

  // 候補選択で他項目をプレフィル（差分だけ手直し）
  const handleSelectSuggestion = (s: ExpenseSuggestion) => {
    onChange({
      ...values,
      name: s.name,
      amount: String(s.amount),
      quantity: String(s.quantity),
      storeId: s.storeId,
      labelId: s.labelId,
      note: s.note ?? '',
    });
  };

  // 店舗選択時：defaultLabelId があり、まだラベル未選択なら自動設定（変更可）
  const handleStoreChange = (store: ExpenseStore | null) => {
    if (!store) {
      set('storeId', '');
      return;
    }
    onChange({
      ...values,
      storeId: store.expenseStoreId,
      labelId: values.labelId || store.defaultLabelId || '',
    });
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '' || /^\d+$/.test(raw)) set('quantity', raw);
  };

  return (
    <Stack spacing={2.5}>
      <FormSection title="購入内容">
        <Stack spacing={2}>
          <DatePicker
            label="購入日"
            value={dateValue}
            onChange={handleDateChange}
            format="YYYY/MM/DD"
            disabled={disabled}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                required: true,
                error: !!errors.purchasedOn,
                helperText: errors.purchasedOn,
                sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
              },
            }}
          />
          <ExpenseNameAutocomplete
            value={values.name}
            onChange={(v) => set('name', v)}
            onSelectSuggestion={handleSelectSuggestion}
            error={!!errors.name}
            helperText={errors.name}
            disabled={disabled}
          />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 7 }}>
              <AmountField
                label="金額"
                required
                value={values.amount}
                onChange={(v) => set('amount', v)}
                error={!!errors.amount}
                helperText={errors.amount}
                disabled={disabled}
                steps={[1, 10, 100, 1000, 10000]}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                label="数量"
                size="small"
                fullWidth
                required
                value={values.quantity}
                onChange={handleQuantityChange}
                error={!!errors.quantity}
                helperText={errors.quantity}
                disabled={disabled}
                inputProps={{ inputMode: 'numeric' }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </Stack>
      </FormSection>

      <FormSection title="分類">
        <Stack spacing={2}>
          <ExpenseStoreSelect
            stores={stores}
            labels={labels}
            value={values.storeId}
            onChange={handleStoreChange}
            onStoreCreated={onStoreCreated}
            required
            error={!!errors.storeId}
            helperText={errors.storeId}
            disabled={disabled}
          />
          <ExpenseLabelSelect
            labels={labels}
            value={values.labelId}
            onChange={(v) => set('labelId', v)}
            onLabelCreated={onLabelCreated}
            required
            error={!!errors.labelId}
            helperText={errors.labelId}
            disabled={disabled}
          />
        </Stack>
      </FormSection>

      <FormSection title="メモ">
        <TextArea
          value={values.note}
          onChange={(v) => set('note', v)}
          maxLength={NOTE_MAX}
          rows={3}
          fullWidth
          disabled={disabled}
          errorMessage={errors.note}
        />
      </FormSection>
    </Stack>
  );
};

export default ExpenseForm;
